import { useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LifeBuoy, Plus, ArrowLeft, Paperclip, X, Loader2, Send,
  Bug, CreditCard, LogIn, Sparkles, MessageSquare, Mail,
  Image as ImageIcon, FileText, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

const CATEGORIES = [
  { k: "access",     label: "Problemas de acceso",     icon: LogIn,        hint: "Login, contraseña, sesión, verificación" },
  { k: "payment",    label: "Problemas de pago",       icon: CreditCard,   hint: "PayPal, facturas, suscripción, reembolso" },
  { k: "bug",        label: "Bug o error",             icon: Bug,          hint: "Algo no funciona como esperabas" },
  { k: "suggestion", label: "Sugerencia / mejora",     icon: Sparkles,     hint: "Ideas o funcionalidades nuevas" },
  { k: "general",    label: "Consulta general",        icon: MessageSquare, hint: "Preguntas sobre el producto" },
  { k: "contact",    label: "Contactar administrador", icon: Mail,         hint: "Mensaje directo al equipo" },
];

const STATUS_META = {
  open:          { label: "Abierto",        cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  in_progress:   { label: "En atención",    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  waiting_user:  { label: "Esperando tu respuesta", cls: "bg-purple-500/15 text-purple-600 dark:text-purple-300" },
  resolved:      { label: "Resuelto",       cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  closed:        { label: "Cerrado",        cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
};

function CategoryIcon({ k, className }) {
  const c = CATEGORIES.find(x => x.k === k);
  const I = c?.icon || MessageSquare;
  return <I className={className} />;
}

export function categoryLabel(k) {
  return CATEGORIES.find(x => x.k === k)?.label || k;
}

export default function Support() {
  const [tab, setTab] = useState("new"); // "new" | "list"
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto kanban-scroll" data-testid="support-page">
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/help" data-testid="support-back-help" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition">
              <ArrowLeft className="h-4 w-4" /> Ayuda
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-blue-500/15 grid place-items-center">
              <LifeBuoy className="h-6 w-6 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Centro de soporte</p>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">¿Cómo podemos ayudarte?</h1>
            </div>
          </div>
          <p className="text-muted-foreground mt-3 max-w-2xl text-sm">
            Cuéntanos qué problema tienes o envíanos un mensaje directo. Te contestamos por email y aquí mismo.
          </p>
        </header>

        <div className="glass-panel rounded-2xl p-1 inline-flex gap-1 mb-6" data-testid="support-tabs">
          <button
            onClick={() => setTab("new")}
            data-testid="support-tab-new"
            className={cn(
              "px-4 h-9 rounded-xl text-sm font-medium inline-flex items-center gap-2 transition",
              tab === "new"
                ? "bg-blue-600 text-white shadow-md"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Plus className="h-4 w-4" /> Nuevo ticket
          </button>
          <button
            onClick={() => setTab("list")}
            data-testid="support-tab-list"
            className={cn(
              "px-4 h-9 rounded-xl text-sm font-medium inline-flex items-center gap-2 transition",
              tab === "list"
                ? "bg-blue-600 text-white shadow-md"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <MessageSquare className="h-4 w-4" /> Mis tickets
          </button>
        </div>

        {tab === "new" ? <NewTicketForm onCreated={(t) => navigate(`/support/${t.ticket_id}`)} /> : <MyTickets />}
      </div>
    </div>
  );
}

function NewTicketForm({ onCreated }) {
  const [category, setCategory] = useState("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]); // {attachment_id, filename, ...}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: async () => (await api.post("/support/tickets", {
      category, subject: subject.trim(), message: message.trim(), attachments,
    })).data,
    onSuccess: (t) => {
      toast.success(`Ticket #${String(t.ticket_number).padStart(4, "0")} creado. Te enviamos un email.`);
      qc.invalidateQueries({ queryKey: ["my-support-tickets"] });
      onCreated(t);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo crear el ticket"),
  });

  const upload = async (files) => {
    const arr = Array.from(files).slice(0, 5 - attachments.length);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      for (const f of arr) {
        if (f.size > 3 * 1024 * 1024) {
          toast.error(`"${f.name}" supera 3 MB.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/support/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const cat = CATEGORIES.find(c => c.k === category);
  const canSubmit = category && subject.trim().length >= 3 && message.trim().length >= 10 && !create.isPending;

  return (
    <div className="glass-panel rounded-2xl p-6 md:p-8 space-y-5" data-testid="support-new-form">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Categoría</label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="support-category" className="mt-1 h-11 bg-white/60 dark:bg-slate-900/40">
            <SelectValue placeholder="Elige una categoría…" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => {
              const I = c.icon;
              return (
                <SelectItem key={c.k} value={c.k} data-testid={`support-category-${c.k}`}>
                  <span className="inline-flex items-center gap-2">
                    <I className="h-4 w-4 text-blue-500" /> {c.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {cat && <p className="text-xs text-muted-foreground mt-1.5">{cat.hint}</p>}
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Asunto</label>
        <Input
          data-testid="support-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Resumen breve del problema…"
          maxLength={200}
          className="mt-1 h-11 bg-white/60 dark:bg-slate-900/40"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Descripción</label>
        <Textarea
          data-testid="support-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Cuéntanos qué pasa. Entre más detalle, más rápido te ayudamos: pasos para reproducirlo, navegador, sistema operativo, capturas de pantalla, etc."
          rows={6}
          maxLength={5000}
          className="mt-1 bg-white/60 dark:bg-slate-900/40"
        />
        <p className="text-[11px] text-muted-foreground mt-1 text-right">{message.length} / 5000</p>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Adjuntos <span className="text-muted-foreground/70">(opcional · máx. 3 MB c/u · hasta 5)</span>
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {attachments.map((a) => (
            <AttachmentChip key={a.attachment_id} att={a} onRemove={() => setAttachments((prev) => prev.filter(x => x.attachment_id !== a.attachment_id))} />
          ))}
          {attachments.length < 5 && (
            <label
              data-testid="support-attach-trigger"
              className={cn(
                "cursor-pointer inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-dashed border-white/60 dark:border-white/20 text-sm text-muted-foreground hover:border-blue-500 hover:text-blue-600 transition",
                uploading && "opacity-60 cursor-wait"
              )}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              Adjuntar archivo
              <input
                ref={fileRef}
                type="file"
                multiple
                className="sr-only"
                data-testid="support-attach-input"
                onChange={(e) => upload(e.target.files)}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/30 dark:border-white/10">
        <p className="text-xs text-muted-foreground">Te notificaremos por email en cuanto te respondamos.</p>
        <Button
          data-testid="support-submit"
          onClick={() => create.mutate()}
          disabled={!canSubmit}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 min-w-[160px]"
        >
          {create.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando…</>
          ) : (
            <><Send className="h-4 w-4 mr-2" /> Enviar ticket</>
          )}
        </Button>
      </div>
    </div>
  );
}

function MyTickets() {
  const { data, isLoading } = useQuery({
    queryKey: ["my-support-tickets"],
    queryFn: async () => (await api.get("/support/tickets")).data,
  });
  const tickets = data?.tickets || [];

  if (isLoading) return <div className="glass-panel rounded-2xl p-8 text-sm text-muted-foreground">Cargando…</div>;

  if (tickets.length === 0) {
    return (
      <div className="glass-panel rounded-2xl p-10 text-center">
        <MessageSquare className="h-8 w-8 text-blue-500 mx-auto mb-2" />
        <p className="font-display text-lg">Aún no tienes tickets</p>
        <p className="text-sm text-muted-foreground mt-1">Cuando envíes uno, aparecerá aquí con toda la conversación.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="support-tickets-list">
      {tickets.map((t) => {
        const meta = STATUS_META[t.status] || STATUS_META.open;
        const ref = "#" + String(t.ticket_number).padStart(4, "0");
        return (
          <Link
            key={t.ticket_id}
            to={`/support/${t.ticket_id}`}
            data-testid={`support-ticket-${t.ticket_id}`}
            className="block glass-card rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-md transition"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl bg-blue-500/10 grid place-items-center flex-none">
                <CategoryIcon k={t.category} className="h-4 w-4 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-muted-foreground">{ref}</span>
                  <span className={cn("text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full", meta.cls)}>
                    {meta.label}
                  </span>
                  {t.unread_by_user && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-rose-500 text-white">
                      Nueva respuesta
                    </span>
                  )}
                </div>
                <p className="font-medium truncate mt-0.5">{t.subject}</p>
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                  {t.last_message_role === "admin" ? "Soporte: " : ""}{t.last_message_preview}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {formatDistanceToNow(new Date(t.last_message_at), { addSuffix: true, locale: es })}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-none" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function AttachmentChip({ att, onRemove }) {
  const Icon = att.is_image ? ImageIcon : FileText;
  const sizeKb = Math.round(att.size / 1024);
  return (
    <span
      data-testid={`support-att-chip-${att.attachment_id}`}
      className="inline-flex items-center gap-2 pl-2 pr-1 h-9 rounded-lg bg-white/60 dark:bg-slate-900/40 border border-white/50 dark:border-white/10 text-sm"
    >
      <Icon className="h-4 w-4 text-blue-500 flex-none" />
      <span className="truncate max-w-[180px]" title={att.filename}>{att.filename}</span>
      <span className="text-[10px] text-muted-foreground">{sizeKb}KB</span>
      {onRemove && (
        <button
          onClick={onRemove}
          data-testid={`support-att-remove-${att.attachment_id}`}
          className="h-6 w-6 rounded-md grid place-items-center hover:bg-rose-500/10 hover:text-rose-600 transition"
          aria-label="Quitar adjunto"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

// Shared helpers for the thread component (used by both user and admin views)
export function TicketAttachmentsList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {attachments.map((a) => (
        <TicketAttachmentItem key={a.attachment_id} att={a} />
      ))}
    </div>
  );
}

function TicketAttachmentItem({ att }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/support/attachments/${att.attachment_id}/download`;
  if (att.is_image) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        data-testid={`ticket-att-${att.attachment_id}`}
        className="block rounded-lg overflow-hidden border border-white/40 dark:border-white/10 hover:opacity-90 transition"
      >
        <img src={url} alt={att.filename} className="h-24 w-auto max-w-[200px] object-cover" />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={`ticket-att-${att.attachment_id}`}
      className="inline-flex items-center gap-2 pl-2 pr-3 h-9 rounded-lg bg-white/60 dark:bg-slate-900/40 border border-white/50 dark:border-white/10 text-sm hover:bg-white/80 dark:hover:bg-slate-900/60 transition"
    >
      <FileText className="h-4 w-4 text-blue-500" />
      <span className="truncate max-w-[180px]">{att.filename}</span>
    </a>
  );
}

// Reusable reply composer used in both user + admin thread views
export function TicketReplyComposer({ ticketId, disabled, onSent, endpoint }) {
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const qc = useQueryClient();

  const upload = async (files) => {
    const arr = Array.from(files).slice(0, 5 - attachments.length);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      for (const f of arr) {
        if (f.size > 3 * 1024 * 1024) { toast.error(`"${f.name}" supera 3 MB.`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/support/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = useMutation({
    mutationFn: async () => (await api.post(endpoint, { message: body.trim(), attachments })).data,
    onSuccess: () => {
      setBody(""); setAttachments([]);
      qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
      qc.invalidateQueries({ queryKey: ["my-support-tickets"] });
      qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      onSent?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo enviar"),
  });

  const canSend = body.trim().length > 0 && !send.isPending && !disabled;

  return (
    <div className="p-4 border-t border-white/30 dark:border-white/10 bg-white/40 dark:bg-slate-900/30" data-testid="ticket-reply-composer">
      {disabled && (
        <p className="text-sm text-muted-foreground mb-3 italic">
          Este ticket está cerrado. Si necesitas más ayuda, crea uno nuevo.
        </p>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a) => (
            <AttachmentChip key={a.attachment_id} att={a} onRemove={() => setAttachments((prev) => prev.filter(x => x.attachment_id !== a.attachment_id))} />
          ))}
        </div>
      )}
      <Textarea
        data-testid="ticket-reply-input"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Escribe tu respuesta…"
        rows={3}
        maxLength={5000}
        disabled={disabled}
        className="bg-white/70 dark:bg-slate-900/40"
      />
      <div className="flex items-center justify-between mt-2 gap-2">
        <label className={cn(
          "cursor-pointer inline-flex items-center gap-2 px-2.5 h-8 rounded-lg text-xs text-muted-foreground hover:text-blue-600 transition",
          (uploading || disabled) && "opacity-50 pointer-events-none"
        )}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          Adjuntar
          <input
            ref={fileRef}
            type="file"
            multiple
            className="sr-only"
            data-testid="ticket-reply-attach"
            onChange={(e) => upload(e.target.files)}
            disabled={uploading || disabled || attachments.length >= 5}
          />
        </label>
        <Button
          data-testid="ticket-reply-send"
          onClick={() => send.mutate()}
          disabled={!canSend}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Enviar</>}
        </Button>
      </div>
    </div>
  );
}

export function TicketThread({ ticket }) {
  const msgs = useMemo(() => ticket?.messages || [], [ticket]);
  return (
    <div className="p-4 md:p-6 space-y-4 max-h-[60vh] overflow-y-auto kanban-scroll" data-testid="ticket-thread">
      {msgs.map((m) => {
        const isAdmin = m.author_role === "admin";
        return (
          <div key={m.message_id} className={cn("flex items-start gap-3", isAdmin && "flex-row-reverse")}
               data-testid={`ticket-msg-${m.message_id}`}>
            <div className={cn(
              "h-8 w-8 rounded-full grid place-items-center text-xs font-semibold flex-none",
              isAdmin ? "bg-blue-500 text-white" : "bg-slate-400/30"
            )}>{(m.author_name?.[0] || "?").toUpperCase()}</div>
            <div className={cn(
              "flex-1 min-w-0 max-w-[85%] rounded-2xl px-4 py-3 text-sm",
              isAdmin
                ? "bg-blue-500/10 border border-blue-500/20"
                : "bg-white/70 dark:bg-slate-900/40 border border-white/50 dark:border-white/10"
            )}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <span className="font-semibold text-foreground">
                  {m.author_name}{isAdmin && <span className="text-blue-600 dark:text-blue-300 ml-1">· Soporte</span>}
                </span>
                <span>·</span>
                <span>{formatDistanceToNow(new Date(m.created_at), { addSuffix: true, locale: es })}</span>
              </div>
              <div className="whitespace-pre-wrap break-words">{m.body}</div>
              <TicketAttachmentsList attachments={m.attachments} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
